import React from 'react'
import { ScrollView } from 'react-native'

import TransparentHeader from '@zet/common/components/header/transparentHeader'
import { HelpDesk } from '@zet/common/components/helpDesk'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { colors as zetColors } from '@zet/design-system'
import { Accordian } from '@zet/design-system/accordian'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import { B5, SH3 } from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { LeadStatusCard } from '@zet/feature-leads/components/leadStatusCard'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { HelpAndSupportType, LeadStatus } from '@zet/utils/Constants'
import { LinearGradient } from 'expo-linear-gradient'
import moment from 'moment'

const LeadsHelp = ({ route }) => {
  const item = route.params.item
  const brandName = route.params.brandName
  const status = item?.status
  const { data: user } = useUserContext()

  const setIntent = ():
    | 'warning'
    | 'active'
    | 'error'
    | 'success'
    | undefined => {
    switch (status) {
      case LeadStatus.LEAD_ADDED:
      case LeadStatus.ACTION_REQUIRED:
        return item?.hasNoUpdate ? 'warning' : 'active'
      case LeadStatus.FULLFILLED:
      case LeadStatus.PARTIALLY_FULLFILLED:
        return 'success'
      case LeadStatus.EXPIRED:
      case LeadStatus.REJECTED:
        return 'error'
      default:
        return undefined
    }
  }

  const setLeadStatusCardContent = () => {
    switch (status) {
      case LeadStatus.LEAD_ADDED:
        return {
          title: item?.isReopened
            ? i18n.t('myLeads.reshared')
            : i18n.t('myLeads.shared'),
          description: i18n.t('leads.linkSharedDescription'),
        }

      case LeadStatus.ACTION_REQUIRED:
        return {
          title:
            item?.applicationAsks.length > 0 &&
            item?.applicationAsks[item?.applicationAsks?.length - 1]?.title,
          description:
            item?.applicationAsks.length > 0 &&
            item?.applicationAsks[item?.applicationAsks?.length - 1]
              ?.description,
        }

      case LeadStatus.REJECTED:
        return {
          title: i18n.t('myLeads.reject'),
          description:
            item?.rejectionReason ||
            i18n.t('leads.rejectionDescription', { brandName: brandName }),
        }

      case LeadStatus.EXPIRED:
        return {
          title: i18n.t('myLeads.expire'),
          description: item?.rejectionReason,
        }

      case LeadStatus.PARTIALLY_FULLFILLED:
        return {
          title: i18n.t('myLeads.partly'),
        }

      case LeadStatus.FULLFILLED:
        return {
          title: i18n.t('myLeads.completed'),
          description: i18n.t('myLeads.completedDes', {
            lastUpdateDate: moment
              .utc(item?.lastUpdateDate)
              .format('DD-MMM-YYYY'),
          }),
        }
    }
  }

  return (
    <Stack className="mb-2 flex-1">
      <LinearGradient
        colors={[zetColors.tertiary[100], zetColors.tertiary[100]]}
        start={[0, 0]}
        end={[1, 0.0]}
        locations={[0.0, 1.0]}
      >
        <TransparentHeader isCloseIcon title={i18n.t('leads.help&Support')} />
        <XStack className="mx-3">
          <YStack className="w-2/4 items-start justify-center">
            <SH3 className=" text-neutral-white100">{item?.name || ''}</SH3>
            <B5 className="text-grays-40">{item?.phoneNumber}</B5>
          </YStack>
          <YStack className="w-2/4 items-end justify-center">
            <SH3
              className="text-grays-10"
              ellipsizeMode="tail"
              numberOfLines={1}
            >
              {brandName}
            </SH3>
            <B5 className="text-grays-40">
              {i18n.t('leads.leadId', { leadId: item?.id })}
            </B5>
          </YStack>
        </XStack>
        <Stack className="mx-3 my-3">
          <LeadStatusCard
            content={setLeadStatusCardContent()}
            intent={setIntent()}
          />
        </Stack>
      </LinearGradient>
      <ScrollView
        className="bg-grays-20 flex-1 "
        showsVerticalScrollIndicator={false}
      >
        <SH3 className=" text-grays-80 my-[20] px-3">
          {i18n.t('myLeads.faq')}
        </SH3>
        <Stack className="bg-white px-3 ">
          {(i18n.t(`leadsFAQ.${status}`) || [])?.map((item, index) => (
            <Accordian
              key={`lead-faq-${index}`}
              className="py-3"
              title={item.question}
              content={item.answer}
              eventName="click_action"
              eventPayload={{
                item_name: 'lead_page_help_and_support_faqs_toggle',
                question: item.question,
              }}
            />
          ))}
        </Stack>

        {APP_NAME !== MY_TEAMS_ADVISOR && (
          <>
            <SH3 className=" text-grays-80 my-[20] px-3 ">
              {i18n.t('myLeads.still')}
            </SH3>
            {i18n.t('myNetwork.helpSupportData').map((e, index) => (
              <HelpDesk
                key={index}
                logo={e.logo}
                title={e.title}
                type={e.type}
                subTitle={e.subTitle}
                complaintsUrl={
                  e.type === HelpAndSupportType.RAISE_A_COMPLAINT
                    ? `https://onecode-asset.s3.ap-south-1.amazonaws.com/prod/raiseDispute.html?onecode=${
                        user?.zetCode
                      }&agentName=${user?.fullName}&agentEmail=${
                        user?.email ? user?.email : ''
                      }&agentNumber=${user?.phoneNumber}&leadID=${
                        item.id
                      }&cxMobNum=${item.phoneNumber}&cxEmail=${
                        item.email ? item.email : ''
                      }&partnerName=${brandName ? brandName : ''}&time=` +
                      Date.now()
                    : ''
                }
                eventName="click_action"
                eventPayload={{
                  item_name: 'lead_page_help_and_support',
                  title: e.title,
                  lead_id: item?.id,
                  lead_status: status,
                  customer_id: item?.customerId,
                  partner_id: item?.partnerId,
                  brand_name: brandName,
                }}
              />
            ))}
          </>
        )}
      </ScrollView>
    </Stack>
  )
}

export { LeadsHelp }
